import { useState, useEffect, useMemo } from "react";
import { useApp } from "../context/AppContext";

// Генератор копі-пейст ШАБЛОНУ СТАТТІ БЛОГУ (розмітка + сучасна Schema під Google + AI-видачу).
// Автор береться з каталогу author.house → у шаблоні вже готове посилання на автора і
// зв'язка сутностей (author @id = <author.house>/<slug>#person — той самий @id, що й на
// сторінці автора). Контент-поля лишаються {{ПЛЕЙСХОЛДЕРАМИ}} для розробника.
// AEO: ImageObject, inLanguage, speakable, author.sameAs, BreadcrumbList, FAQPage.

const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const P = (mode, token, demo) => (mode === "preview" ? demo : `{{${token}}}`);

const DEMO_COVER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1200' height='630'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%236366f1'/%3E%3Cstop offset='1' stop-color='%23a855f7'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='1200' height='630' fill='url(%23g)'/%3E%3Ctext x='600' y='330' font-family='Arial' font-size='44' fill='%23fff' text-anchor='middle' font-weight='bold'%3E%7B%7B URL_ОБКЛАДИНКИ %7D%7D%3C/text%3E%3C/svg%3E";

function buildTemplate(a, org, opt, mode) {
  const acc = opt.accent || "#6366f1";
  const dark = opt.theme === "dark";
  const bg = dark ? "#0f1221" : "#ffffff";
  const fg = dark ? "#e8e9f3" : "#1a1a2e";
  const mut = dark ? "#9aa0b4" : "#6b7280";
  const line = dark ? "rgba(255,255,255,.1)" : "#ececf3";
  const soft = dark ? "rgba(255,255,255,.04)" : "#f7f7fb";
  const lang = opt.lang || "uk";

  // Автор: реальні дані (якщо обрано) або плейсхолдери.
  const aName = a.name ? esc(a.name) : P(mode, "ІМʼЯ_АВТОРА", "Іван Петренко");
  // Дві РІЗНІ ссилки автора: aHouse — ЗОВНІШНІЙ профіль author.house (→ sameAs), aSite — ВНУТРІШНЯ сторінка автора на цьому сайті (→ author.url, байлайн rel="author").
  const aHouse = a.url ? esc(a.url) : P(mode, "URL_АВТОРА_AUTHORHOUSE", "https://author.house/ivan-petrenko");
  const aSite = a.siteUrl ? esc(a.siteUrl) : P(mode, "URL_СТОРІНКИ_АВТОРА_НА_САЙТІ", "https://your-site.com/author/ivan-petrenko");
  const aRole = a.jobTitle ? esc(a.jobTitle) : (mode === "preview" ? "Експерт з бронезахисту" : "{{ПОСАДА_АВТОРА}}");
  const aPhoto = a.photo || (mode === "preview"
    ? "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect width='80' height='80' fill='%236366f1'/%3E%3Ctext x='40' y='52' font-size='36' fill='%23fff' text-anchor='middle' font-family='Arial' font-weight='bold'%3EА%3C/text%3E%3C/svg%3E"
    : "{{URL_ФОТО_АВТОРА}}");

  const orgName = org.name ? esc(org.name) : P(mode, "НАЗВА_САЙТУ", "Назва сайту");
  const orgLogo = org.logo ? esc(org.logo) : P(mode, "URL_ЛОГО_САЙТУ", "https://site.com/logo.png");
  const siteUrl = P(mode, "URL_САЙТУ", "https://your-site.com");
  const blogUrl = P(mode, "URL_БЛОГУ", "https://your-site.com/blog/");

  const url = P(mode, "URL_СТАТТІ", "https://your-site.com/blog/yak-obraty-bronezhylet");
  const headline = P(mode, "ЗАГОЛОВОК_СТАТТІ", "Як обрати бронежилет: клас захисту, вага та матеріали");
  const descr = P(mode, "КОРОТКИЙ_ОПИС", "Повний гайд: класи бронезахисту за ДСТУ, вибір плит, вага й комфорт — поради від практика.");
  const coverUrl = mode === "preview" ? "https://your-site.com/img/cover.jpg" : "{{URL_ОБКЛАДИНКИ}}";
  const coverSrc = mode === "preview" ? DEMO_COVER : "{{URL_ОБКЛАДИНКИ}}";
  const datePub = P(mode, "ДАТА_ПУБЛІКАЦІЇ", opt.today);
  const dateMod = P(mode, "ДАТА_ОНОВЛЕННЯ", opt.today);
  const keywords = P(mode, "КЛЮЧОВІ_СЛОВА", "бронежилет, клас захисту, бронеплити");
  const dateHuman = mode === "preview" ? "16 жовтня 2026" : "{{ДАТА_ПУБЛІКАЦІЇ}}";

  const q1 = P(mode, "ПИТАННЯ_1", "Який клас бронежилета обрати?");
  const an1 = P(mode, "ВІДПОВІДЬ_1", "Для цивільного захисту зазвичай достатньо 1 класу, для бойових завдань — 4 клас і вище за ДСТУ.");
  const q2 = P(mode, "ПИТАННЯ_2", "Скільки важить бронежилет?");
  const an2 = P(mode, "ВІДПОВІДЬ_2", "Залежно від класу й матеріалу плит — від ~2 кг (поліетилен) до 8+ кг (сталь). Керамо-композит — оптимум ваги й захисту.");

  const body = mode === "preview"
    ? `<p>Бронежилет — ключовий елемент індивідуального захисту. У цьому гайді розберемо, як не помилитися з класом, вагою та типом плит.</p>
    <h2>Класи захисту за ДСТУ</h2>
    <p>Українська класифікація визначає кілька рівнів — від протиуламкового до захисту від бронебійних куль.</p>
    <ul><li>1 клас — пістолетні набої;</li><li>4 клас — автоматні кулі зі сталевим осердям;</li><li>5–6 клас — бронебійні.</li></ul>
    <h2>Вага і комфорт</h2>
    <p>Легші керамічні або поліетиленові плити дорожчі, але суттєво знижують втому. Для тривалого носіння це критично.</p>`
    : "{{ТІЛО_СТАТТІ_HTML}}";

  const css = `
.spa-art{--acc:${acc};font-family:-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  max-width:760px;margin:0 auto;color:${fg};background:${bg};line-height:1.7;box-sizing:border-box;padding:8px}
.spa-art *{box-sizing:border-box}
.spa-art .spa-crumbs{font-size:13px;color:${mut};margin-bottom:14px}
.spa-art .spa-crumbs a{color:${mut};text-decoration:none}
.spa-art h1{font-size:34px;font-weight:800;line-height:1.2;margin:0 0 16px}
.spa-art .spa-meta{display:flex;align-items:center;gap:12px;flex-wrap:wrap;padding:12px 0;border-top:1px solid ${line};border-bottom:1px solid ${line};font-size:14px;color:${mut}}
.spa-art .spa-ava{width:44px;height:44px;border-radius:50%;object-fit:cover;border:2px solid var(--acc)}
.spa-art .spa-au{color:var(--acc);font-weight:700;text-decoration:none}
.spa-art .spa-role{color:${mut}}
.spa-art .spa-ext{color:${mut};text-decoration:underline}
.spa-art .spa-cover{width:100%;height:auto;border-radius:16px;margin:22px 0;display:block;aspect-ratio:1200/630;object-fit:cover;background:${soft}}
.spa-art .spa-body{font-size:17px;color:${fg}}
.spa-art .spa-body h2{font-size:24px;font-weight:700;margin:28px 0 10px}
.spa-art .spa-body p{margin:0 0 16px}
.spa-art .spa-body ul,.spa-art .spa-body ol{margin:0 0 16px;padding-left:22px}
.spa-art .spa-body li{margin-bottom:6px}
.spa-art .spa-body a{color:var(--acc)}
.spa-art .spa-faq{margin-top:34px;border-top:1px solid ${line};padding-top:22px}
.spa-art .spa-faq h2{font-size:24px;font-weight:700;margin:0 0 14px}
.spa-art .spa-faq details{border:1px solid ${line};border-radius:12px;padding:12px 16px;margin-bottom:10px;background:${soft}}
.spa-art .spa-faq summary{font-weight:700;cursor:pointer;color:${fg}}
.spa-art .spa-faq details p{margin:10px 0 0;color:${mut};font-size:15px}
@media(max-width:560px){.spa-art h1{font-size:26px}.spa-art .spa-body{font-size:16px}}`.trim();

  // ── BlogPosting (сучасна розмітка статті + AEO) ──
  const jsonld = `{
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "@id": "${url}#article",
  "mainEntityOfPage": { "@type": "WebPage", "@id": "${url}" },
  "headline": "${headline}",
  "description": "${descr}",
  "inLanguage": "${lang}",
  "image": {
    "@type": "ImageObject",
    "url": "${coverUrl}",
    "width": 1200,
    "height": 630
  },
  "datePublished": "${datePub}",
  "dateModified": "${dateMod}",
  "author": {
    "@type": "Person",
    "@id": "${aHouse}#person",
    "name": "${aName}",
    "url": "${aSite}",
    "jobTitle": "${aRole}",
    "sameAs": ["${aHouse}"]
  },
  "publisher": {
    "@type": "Organization",
    "name": "${orgName}",
    "url": "${siteUrl}",
    "logo": { "@type": "ImageObject", "url": "${orgLogo}" }
  },
  "speakable": {
    "@type": "SpeakableSpecification",
    "cssSelector": ["h1", ".spa-body"]
  },
  "keywords": "${keywords}"
}`;

  const breadcrumbLd = `{
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Головна", "item": "${siteUrl}" },
    { "@type": "ListItem", "position": 2, "name": "Блог", "item": "${blogUrl}" },
    { "@type": "ListItem", "position": 3, "name": "${headline}", "item": "${url}" }
  ]
}`;

  const faqLd = `{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    { "@type": "Question", "name": "${q1}", "acceptedAnswer": { "@type": "Answer", "text": "${an1}" } },
    { "@type": "Question", "name": "${q2}", "acceptedAnswer": { "@type": "Answer", "text": "${an2}" } }
  ]
}`;

  const crumbs = opt.showCrumbs
    ? `  <nav class="spa-crumbs"><a href="${siteUrl}">Головна</a> › <a href="${blogUrl}">Блог</a> › ${headline}</nav>\n` : "";
  const coverTag = opt.showCover
    ? `  <img class="spa-cover" src="${coverSrc}" alt="${headline}" loading="lazy">\n` : "";
  const photoTag = opt.showPhoto
    ? `<img class="spa-ava" src="${aPhoto}" alt="${aName}" loading="lazy">` : "";
  const faqBlock = opt.showFaq
    ? `  <!-- FAQ для AI-видачі (ChatGPT, AI Overviews, Perplexity легко цитують Q&A) -->
  <section class="spa-faq">
    <h2>Часті запитання</h2>
    <details><summary>${q1}</summary><p>${an1}</p></details>
    <details><summary>${q2}</summary><p>${an2}</p></details>
  </section>\n` : "";

  const legend = `<!-- ============================================================
     ШАБЛОН СТАТТІ БЛОГУ · згенеровано Spalah
     Сучасна Schema (BlogPosting${opt.showCrumbs ? " + BreadcrumbList" : ""}${opt.showFaq ? " + FAQPage" : ""}),
     оптимізована під Google rich results і AI-видачу (AEO).
     РОЗРОБНИКУ: встав блок у шаблон статті й заміни всі {{ПЛЕЙСХОЛДЕРИ}}.
     ✅ Автор: ВНУТРІШНЯ ссилка (byline rel="author" + author.url) → сторінка автора на ЦЬОМУ сайті;
        ЗОВНІШНЯ (sameAs + 2-е посилання "профіль ↗") → author.house. @id = сутність author.house (E-E-A-T).
        Якщо автор обраний у Spalah — не чіпай; інакше заміни {{URL_СТОРІНКИ_АВТОРА_НА_САЙТІ}} і {{URL_АВТОРА_AUTHORHOUSE}}.
     🔧 Заміни:
        {{URL_СТАТТІ}}        — повний URL сторінки (canonical + @id)
        {{URL_САЙТУ}}         — URL головної (напр. https://site.com)
        {{ЗАГОЛОВОК_СТАТТІ}}  — H1, до ~60 символів
        {{КОРОТКИЙ_ОПИС}}     — опис/лід, 150–160 символів
        {{URL_ОБКЛАДИНКИ}}    — головне зображення, 1200×630
        {{ДАТА_ПУБЛІКАЦІЇ}}   — ISO 8601, напр. ${opt.today}
        {{ДАТА_ОНОВЛЕННЯ}}    — ISO 8601
        {{КЛЮЧОВІ_СЛОВА}}     — 3–6 ключів через кому
        {{ТІЛО_СТАТТІ_HTML}}  — HTML-вміст (H2, абзаци, списки)${opt.showFaq ? `
        {{ПИТАННЯ_1..2}} / {{ВІДПОВІДЬ_1..2}} — реальні Q&A (для AI-видачі)` : ""}
     Мова статті (inLanguage): "${lang}" — зміни за потреби.
     ============================================================ -->\n`;

  const scripts = `<!-- Schema.org BlogPosting · author.url = ВНУТРІШНЯ сторінка автора на сайті · author.sameAs = ЗОВНІШНІЙ author.house · @id = сутність author.house -->
<script type="application/ld+json">
${jsonld}
</script>${opt.showCrumbs ? `
<script type="application/ld+json">
${breadcrumbLd}
</script>` : ""}${opt.showFaq ? `
<script type="application/ld+json">
${faqLd}
</script>` : ""}`;

  const html = `<article class="spa-art" lang="${lang}">
  <style>
${css}
  </style>
${crumbs}  <h1>${headline}</h1>
  <!-- ✅ АВТОР. Дві ссилки: (1) ВНУТРІШНЯ rel="author" → сторінка автора на ЦЬОМУ сайті; (2) ЗОВНІШНЯ "профіль ↗" → author.house. Не змінювати. -->
  <div class="spa-meta">
    ${photoTag}
    <span>Автор: <a class="spa-au" href="${aSite}" rel="author">${aName}</a></span>
    <span class="spa-role">· ${aRole}</span>
    <span>· <a class="spa-ext" href="${aHouse}" rel="noopener" target="_blank">профіль експерта ↗</a></span>
    <span>· <time datetime="${datePub}">${dateHuman}</time></span>
  </div>
${coverTag}  <div class="spa-body">
    ${body}
  </div>
${faqBlock}</article>
${scripts}`;

  const altLink = opt.showMd
    ? `<!-- ↓ ДОДАЙТЕ ЦЕЙ РЯДОК У <head> СТОРІНКИ — щоб ІІ-краулери знайшли markdown-версію статті: -->
<link rel="alternate" type="text/markdown" href="${url}.md">\n\n` : "";

  return { html: (mode === "code" ? legend + altLink : "") + html, bg };
}

// ── Markdown-дзеркало статті (для ІІ-агентів: чистий парсинг + YAML-метадані) ──
function buildMarkdown(a, org, opt, mode) {
  const lang = opt.lang || "uk";
  const aName = a.name || P(mode, "ІМʼЯ_АВТОРА", "Іван Петренко");
  const aHouse = a.url || P(mode, "URL_АВТОРА_AUTHORHOUSE", "https://author.house/ivan-petrenko");
  const aSite = a.siteUrl || P(mode, "URL_СТОРІНКИ_АВТОРА_НА_САЙТІ", "https://your-site.com/author/ivan-petrenko");
  const aRole = a.jobTitle || (mode === "preview" ? "Експерт з бронезахисту" : "{{ПОСАДА_АВТОРА}}");
  const url = P(mode, "URL_СТАТТІ", "https://your-site.com/blog/yak-obraty-bronezhylet");
  const headline = P(mode, "ЗАГОЛОВОК_СТАТТІ", "Як обрати бронежилет: клас захисту, вага та матеріали");
  const descr = P(mode, "КОРОТКИЙ_ОПИС", "Повний гайд: класи бронезахисту за ДСТУ, вибір плит, вага й комфорт — поради від практика.");
  const datePub = P(mode, "ДАТА_ПУБЛІКАЦІЇ", opt.today);
  const dateMod = P(mode, "ДАТА_ОНОВЛЕННЯ", opt.today);
  const keywords = P(mode, "КЛЮЧОВІ_СЛОВА", "бронежилет, клас захисту, бронеплити");
  const q1 = P(mode, "ПИТАННЯ_1", "Який клас бронежилета обрати?");
  const an1 = P(mode, "ВІДПОВІДЬ_1", "Для цивільного захисту зазвичай достатньо 1 класу, для бойових завдань — 4 клас і вище за ДСТУ.");
  const q2 = P(mode, "ПИТАННЯ_2", "Скільки важить бронежилет?");
  const an2 = P(mode, "ВІДПОВІДЬ_2", "Залежно від класу й матеріалу плит — від ~2 кг (поліетилен) до 8+ кг (сталь).");
  const body = mode === "preview"
    ? `Бронежилет — ключовий елемент індивідуального захисту. Нижче — як обрати клас, вагу та тип плит.

## Класи захисту за ДСТУ

- **1 клас** — пістолетні набої;
- **4 клас** — автоматні кулі зі сталевим осердям;
- **5–6 клас** — бронебійні.

## Вага і комфорт

Легші керамічні або поліетиленові плити дорожчі, але суттєво знижують втому.`
    : "{{ТІЛО_СТАТТІ_MARKDOWN}}";

  const front = `---
title: "${headline}"
description: "${descr}"
author: "${aName}"
author_url: "${aSite}"
author_profile: "${aHouse}"
date: "${datePub}"
modified: "${dateMod}"
lang: "${lang}"
canonical: "${url}"
keywords: "${keywords}"
---`;

  const faqMd = opt.showFaq ? `

## Часті запитання

**${q1}**

${an1}

**${q2}**

${an2}` : "";

  const instr = `

<!-- ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  ІНСТРУКЦІЯ ДЛЯ РОЗРОБНИКА · приберіть цей блок перед публікацією
  1. Віддавайте цей контент за адресою:  ${url}.md
     (markdown-дзеркало статті — той самий матеріал, що й HTML-версія).
  2. Заголовок відповіді:  Content-Type: text/markdown; charset=utf-8
  3. У HTML-версії статті у <head> додайте тег-дискавері:
     <link rel="alternate" type="text/markdown" href="${url}.md">
     (щоб ІІ-краулери знайшли markdown-версію напряму зі сторінки).
  4. Автор / дата / заголовок мають збігатися з HTML і Schema — єдина сутність.
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ -->`;

  return `${front}

# ${headline}

> ${descr}

**Автор:** [${aName}](${aSite}) · ${aRole} · [профіль](${aHouse}) · ${datePub}

${body}${faqMd}${mode === "code" ? instr : ""}`;
}

export default function BlogTemplateGenerator({ onClose }) {
  const { t, dark, proj, flash, apiFetch } = useApp();
  const today = "2026-10-16";
  const [authors, setAuthors] = useState([]);
  const [ahBase, setAhBase] = useState("https://author.house");
  const [sel, setSel] = useState("");
  const [manual, setManual] = useState({ name: "", url: "", jobTitle: "", photo: "" });
  const [authorSiteUrl, setAuthorSiteUrl] = useState(""); // внутрішня сторінка автора на сайті клієнта (→ author.url, байлайн rel=author)
  const [org, setOrg] = useState({ name: proj?.name || "", logo: "" });
  const [opt, setOpt] = useState({ accent: "#6366f1", theme: "light", lang: "uk", showCover: true, showCrumbs: true, showPhoto: true, showFaq: true, showMd: true, today });
  const [tab, setTab] = useState("preview");
  const [copied, setCopied] = useState(false);
  const [help, setHelp] = useState(false);

  useEffect(() => {
    apiFetch(`/api/authors`).then((r) => r.json()).then((d) => {
      if (d && Array.isArray(d.authors)) { setAuthors(d.authors); if (d.base) setAhBase(d.base.replace(/\/+$/, "")); }
    }).catch(() => {});
  }, []); // eslint-disable-line

  const setO = (k, v) => setOpt((p) => ({ ...p, [k]: v }));

  const author = useMemo(() => {
    const a = authors.find((x) => x.slug === sel);
    if (a) return { name: a.name, url: `${ahBase}/${a.slug}`, siteUrl: authorSiteUrl, jobTitle: a.job_title || "", photo: a.photo || "" };
    return { name: manual.name, url: manual.url, siteUrl: authorSiteUrl, jobTitle: manual.jobTitle, photo: manual.photo };
  }, [authors, sel, ahBase, manual, authorSiteUrl]);

  const code = useMemo(() => buildTemplate(author, org, opt, "code").html, [author, org, opt]);
  const md = useMemo(() => buildMarkdown(author, org, opt, "code"), [author, org, opt]);
  const preview = useMemo(() => buildTemplate(author, org, opt, "preview"), [author, org, opt]);
  const previewDoc = `<!doctype html><html lang="uk"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head><body style="margin:0;padding:28px;background:${dark ? "#0b0d17" : "#eef0f6"}">${preview.html}</body></html>`;

  const copy = () => {
    const txt = tab === "md" ? md : code;
    try { navigator.clipboard.writeText(txt); setCopied(true); setTimeout(() => setCopied(false), 1600); flash && flash(tab === "md" ? "Markdown скопійовано" : "Шаблон скопійовано"); } catch {}
  };

  const T = t || {};
  const inp = { width: "100%", background: T.bg || "#fafafa", color: T.text, border: `1px solid ${T.border || "#e5e7eb"}`, borderRadius: 8, padding: "8px 10px", fontFamily: "inherit", fontSize: 13, boxSizing: "border-box" };
  const lbl = { fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".04em", display: "block", marginBottom: 4 };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,.55)", display: "flex", justifyContent: "center", padding: 20, overflow: "auto" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
      <div style={{ background: dark ? "#14162a" : "#fff", color: T.text, borderRadius: 16, width: "100%", maxWidth: 1180, maxHeight: "94vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,.4)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: `1px solid ${T.border}`, flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>📝 Шаблон статті блогу</div>
          <div style={{ fontSize: 12, color: T.textMuted }}>HTML + Schema + markdown-дзеркало для AI · {"{{плейсхолдери}}"} міняє розробник</div>
          <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 12px", cursor: "pointer", color: T.textMuted, fontFamily: "inherit", fontWeight: 600 }}>✕ Закрити</button>
        </div>

        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          <div style={{ width: 420, flexShrink: 0, overflowY: "auto", padding: 18, borderRight: `1px solid ${T.border}` }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Автор статті</div>
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>Обрати з каталогу author.house</label>
              <select value={sel} onChange={(e) => setSel(e.target.value)} style={inp}>
                <option value="">— ввести вручну / плейсхолдери —</option>
                {authors.map((a) => <option key={a.slug} value={a.slug}>{a.name}{a.job_title ? ` · ${a.job_title}` : ""}</option>)}
              </select>
              <div style={{ fontSize: 10.5, color: T.textMuted, marginTop: 4, lineHeight: 1.4 }}>
                {sel
                  ? <>ЗОВНІШНІЙ профіль (author.house): <a href={author.url} target="_blank" rel="noreferrer" style={{ color: "#6366f1", fontWeight: 600 }}>{author.url}</a> → йде у <code>sameAs</code> + посилання «профіль ↗». У Schema <code>@id …#person</code> — та сама сутність, що й на author.house.</>
                  : <>Без вибору — плейсхолдери <code>{"{{ІМʼЯ_АВТОРА}}"}</code> / <code>{"{{URL_АВТОРА_AUTHORHOUSE}}"}</code>. Авторів створюють у «👤 Сторінка автора».</>}
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>URL сторінки автора на ЦЬОМУ сайті (внутрішня)</label>
              <input value={authorSiteUrl} onChange={(e) => setAuthorSiteUrl(e.target.value)} placeholder="https://site.com/author/ivan" style={inp} />
              <div style={{ fontSize: 10.5, color: T.textMuted, marginTop: 4, lineHeight: 1.4 }}>Внутрішня сторінка автора на сайті клієнта → <code>author.url</code> + головне посилання байлайну (<code>rel="author"</code>). author.house при цьому лишається у <code>sameAs</code> (зовнішній ресурс). Порожнє → плейсхолдер <code>{"{{URL_СТОРІНКИ_АВТОРА_НА_САЙТІ}}"}</code>.</div>
            </div>
            {!sel && (
              <div style={{ padding: "10px 12px", border: `1px dashed ${T.border}`, borderRadius: 10, marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>Або впишіть автора вручну:</div>
                <input value={manual.name} onChange={(e) => setManual((p) => ({ ...p, name: e.target.value }))} placeholder="Ім'я автора" style={{ ...inp, marginBottom: 6 }} />
                <input value={manual.url} onChange={(e) => setManual((p) => ({ ...p, url: e.target.value }))} placeholder="URL профілю author.house (→ sameAs, зовнішній)" style={{ ...inp, marginBottom: 6 }} />
                <input value={manual.jobTitle} onChange={(e) => setManual((p) => ({ ...p, jobTitle: e.target.value }))} placeholder="Посада (необов'язково)" style={inp} />
              </div>
            )}

            <div style={{ fontWeight: 700, fontSize: 13, margin: "8px 0 10px" }}>Видавець (сайт)</div>
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>Назва сайту / організації</label>
              <input value={org.name} onChange={(e) => setOrg((p) => ({ ...p, name: e.target.value }))} placeholder={proj?.name || "Назва сайту"} style={inp} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>URL логотипу сайту</label>
              <input value={org.logo} onChange={(e) => setOrg((p) => ({ ...p, logo: e.target.value }))} placeholder="https://site.com/logo.png" style={inp} />
            </div>

            <div style={{ fontWeight: 700, fontSize: 13, margin: "16px 0 8px" }}>Вигляд і Schema</div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
              <label style={{ fontSize: 12, color: T.textMuted, display: "flex", alignItems: "center", gap: 6 }}>
                Колір <input type="color" value={opt.accent} onChange={(e) => setO("accent", e.target.value)} style={{ width: 34, height: 26, border: "none", background: "none", cursor: "pointer" }} />
              </label>
              <label style={{ fontSize: 12, color: T.textMuted }}>Тема
                <select value={opt.theme} onChange={(e) => setO("theme", e.target.value)} style={{ ...inp, width: "auto", marginLeft: 6, padding: "4px 8px" }}>
                  <option value="light">світла</option><option value="dark">темна</option>
                </select>
              </label>
              <label style={{ fontSize: 12, color: T.textMuted }}>Мова
                <select value={opt.lang} onChange={(e) => setO("lang", e.target.value)} style={{ ...inp, width: "auto", marginLeft: 6, padding: "4px 8px" }}>
                  <option value="uk">uk</option><option value="ru">ru</option><option value="en">en</option>
                </select>
              </label>
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: T.textMuted }}>
              {[["showCover", "Обкладинка"], ["showCrumbs", "Хлібні крихти + BreadcrumbList"], ["showPhoto", "Фото автора"], ["showFaq", "FAQ + FAQPage (для AI)"], ["showMd", "Markdown-версія (для AI) + <link>"]].map(([k, l]) => (
                <label key={k} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                  <input type="checkbox" checked={opt[k]} onChange={(e) => setO(k, e.target.checked)} /> {l}
                </label>
              ))}
            </div>
            <div style={{ fontSize: 10.5, color: T.textMuted, marginTop: 12, lineHeight: 1.5, background: dark ? "rgba(99,102,241,.08)" : "rgba(99,102,241,.06)", borderRadius: 8, padding: "8px 10px" }}>
            <b>HTML + Schema</b>: BlogPosting (author @id, ImageObject, inLanguage, speakable), BreadcrumbList, FAQPage — під Google rich results. <b>Markdown (AI)</b>: окрема вкладка — чисте markdown-дзеркало з YAML-метаданими для нейромереж + тег <code>&lt;link rel="alternate" type="text/markdown"&gt;</code> у HTML, щоб ІІ знайшли .md-версію.
            </div>

            {/* ── Підказка: де зберігати markdown-версію й навіщо ── */}
            <button onClick={() => setHelp((v) => !v)} style={{ marginTop: 12, width: "100%", textAlign: "left", background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "9px 12px", cursor: "pointer", color: T.text, fontFamily: "inherit", fontWeight: 700, fontSize: 12.5, display: "flex", alignItems: "center", gap: 8 }}>
              <span>{help ? "▾" : "▸"}</span> ❓ Де зберігати markdown-версію й навіщо
            </button>
            {help && (() => {
              const codeBox = { fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11, background: dark ? "#0b0d17" : "#f4f5fb", border: `1px solid ${T.border}`, borderRadius: 7, padding: "7px 9px", margin: "4px 0", color: dark ? "#c7d2fe" : "#3730a3", whiteSpace: "pre-wrap", wordBreak: "break-all" };
              const h = { fontWeight: 800, fontSize: 12, margin: "12px 0 4px", color: T.text };
              const p = { fontSize: 11.5, color: T.textMuted, lineHeight: 1.55, margin: "0 0 4px" };
              return (
                <div style={{ marginTop: 6, padding: "12px 14px", border: `1px solid ${T.border}`, borderRadius: 10, background: dark ? "rgba(255,255,255,.02)" : "#fcfcff" }}>
                  <div style={h}>Навіщо це</div>
                  <p style={p}>ІІ (ChatGPT, Google AI Overviews, Perplexity) розбирає сторінку, а не читає як людина. У HTML багато зайвого (меню, скрипти) — ІІ плутається. Markdown чистий → нейромережа точніше розуміє статтю й <b>частіше цитує</b> її у відповідях. Безкоштовний плюс до AI-видачі без ризику санкцій.</p>

                  <div style={h}>Де зберігається</div>
                  <p style={p}>Та сама стаття, але за другою адресою — поряд зі звичайною, з <code>.md</code> на кінці. Окремий файл/сервіс не потрібен:</p>
                  <div style={codeBox}>люди + Google:  /blog/stattia{"\n"}ІІ (markdown):   /blog/stattia.md</div>

                  <div style={h}>Як розмістити (розробнику)</div>
                  <p style={p}>1. Налаштувати, щоб за <code>…/stattia.md</code> віддавався markdown-текст (роут у CMS, статичний файл або правило на сервері).<br/>2. Заголовок відповіді: <code>Content-Type: text/markdown; charset=utf-8</code><br/>3. Вміст — з вкладки «⬇ Markdown (AI)», із підставленими реальними даними замість {"{{…}}"}.</p>

                  <div style={h}>Як дати ІІ шлях</div>
                  <p style={p}>Нічого окремо розсилати не треба. У <code>&lt;head&gt;</code> звичайної сторінки — один рядок-вказівник (генерується автоматично):</p>
                  <div style={codeBox}>&lt;link rel="alternate" type="text/markdown"{"\n"}      href="https://site.com/blog/stattia.md"&gt;</div>
                  <p style={p}>ІІ-краулер заходить на сторінку → бачить тег → забирає <code>.md</code>-версію.</p>
                  <p style={{ ...p, color: dark ? "#fcd34d" : "#b45309" }}>⚠️ Раніше для цього робили <code>llms.txt</code> — Google офіційно сказав, що не використовує його, а AI-краулери ігнорують. Тому надійніше вказівник <code>&lt;link rel="alternate"&gt;</code> прямо на сторінці.</p>

                  <div style={h}>Головне правило</div>
                  <p style={p}>Заголовок, автор і дата в HTML, Schema і markdown мають <b>збігатися</b> — тоді для ІІ це одне авторитетне джерело з одним автором.</p>
                </div>
              );
            })()}
          </div>

          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", gap: 8, padding: "10px 16px", borderBottom: `1px solid ${T.border}`, alignItems: "center" }}>
              {[["preview", "👁 Прев'ю"], ["code", "</> HTML + Schema"], ["md", "⬇ Markdown (AI)"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} style={{ background: tab === k ? "linear-gradient(135deg,#6366f1,#a855f7)" : "none", color: tab === k ? "#fff" : T.textMuted, border: `1px solid ${tab === k ? "#6366f1" : T.border}`, borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{l}</button>
              ))}
              <button onClick={copy} style={{ marginLeft: "auto", background: copied ? "#16a34a" : "linear-gradient(135deg,#6366f1,#a855f7)", color: "#fff", border: "none", borderRadius: 8, padding: "6px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{copied ? "✓ Скопійовано" : (tab === "md" ? "⧉ Скопіювати Markdown" : "⧉ Скопіювати шаблон")}</button>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: "auto", background: dark ? "#0b0d17" : "#eef0f6" }}>
              {tab === "preview" ? (
                <iframe title="preview" srcDoc={previewDoc} style={{ width: "100%", height: "100%", minHeight: 520, border: "none", display: "block" }} />
              ) : (
                <textarea readOnly value={tab === "md" ? md : code} style={{ width: "100%", height: "100%", minHeight: 520, border: "none", padding: 16, fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12, lineHeight: 1.5, background: dark ? "#0b0d17" : "#fff", color: dark ? "#e8e9f3" : "#1a1a2e", resize: "none", boxSizing: "border-box" }} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
